import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Server, CheckCircle, XCircle, Target, BookOpen, Zap, ArrowRight,
  RotateCcw, Lightbulb, ChevronRight, Timer, BarChart3, Flame, Star,
  LogIn, LogOut, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';
import CertPaywallModal from '@/components/CertPaywallModal';
import { useAuth } from '@/context/AuthContext';
import { useCertAccess } from '@/lib/certAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CERT_CODE = 'az-104';
const CERT_NAME = 'AZ-104: Microsoft Azure Administrator Associate';
const CERT_PRICE = 49;
const SIM_TIME = 6000; // 115 minutes
const API_PREFIX = `${API_URL}/api/az104`;

const PRACTICE_MODES = [
  { id: 'quick', name: 'Quick Practice', icon: Zap, desc: '10 random questions', questions: 10, color: 'bg-blue-500' },
  { id: 'topic', name: 'Topic Mode', icon: BookOpen, desc: 'Focus on specific skill area', questions: 10, color: 'bg-indigo-500' },
  { id: 'simulation', name: 'Exam Simulation', icon: Timer, desc: '55 questions, 100-min timer', questions: 55, color: 'bg-violet-600' },
  { id: 'weak', name: 'Weak Areas', icon: Target, desc: 'Practice your weak spots', questions: 10, color: 'bg-orange-500' },
  { id: 'flashcards', name: 'Flashcards', icon: RotateCcw, desc: 'Review key concepts', questions: 0, color: 'bg-pink-500' },
];

export default function AZ104ExamPrepPage() {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [userId] = useState(() => {
    const storedId = localStorage.getItem('az104_user_id');
    return storedId || `user_${Date.now()}`;
  });
  const effectiveUserId = user?.id || userId;
  const proEmail = user?.email || localStorage.getItem('paywall_email') || '';
  const { unlocked: proUnlocked, refresh: refreshAccess } = useCertAccess(CERT_CODE, proEmail);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('Exam Simulation');

  const requirePro = (feature, cb) => {
    if (proUnlocked) return cb();
    setPaywallFeature(feature);
    setPaywallOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) refreshAccess();
  }, [refreshAccess]);

  const [progress, setProgress] = useState(null);
  const [skillAreas, setSkillAreas] = useState({});
  const [platformStats, setPlatformStats] = useState(null);
  const [examTips, setExamTips] = useState(null);

  const [practiceMode, setPracticeMode] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [practiceStats, setPracticeStats] = useState({ correct: 0, total: 0 });

  const [simulationActive, setSimulationActive] = useState(false);
  const [simulationTime, setSimulationTime] = useState(SIM_TIME);
  const [simulationAnswers, setSimulationAnswers] = useState({});
  const [simulationResult, setSimulationResult] = useState(null);

  const [flashcards, setFlashcards] = useState([]);
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [showFlashcardBack, setShowFlashcardBack] = useState(false);

  useEffect(() => {
    localStorage.setItem('az104_user_id', effectiveUserId);
    fetchProgress();
    fetchSkillAreas();
    fetchPlatformStats();
    fetchExamTips();
  }, [effectiveUserId]);

  const openAuth = (tab = 'login') => {
    setAuthModalTab(tab);
    setShowAuthModal(true);
  };

  useEffect(() => {
    let interval;
    if (simulationActive && simulationTime > 0) {
      interval = setInterval(() => setSimulationTime(prev => prev - 1), 1000);
    } else if (simulationTime === 0 && simulationActive) {
      submitSimulation();
    }
    return () => clearInterval(interval);
  }, [simulationActive, simulationTime]);

  const fetchProgress = async () => {
    try {
      const res = await fetch(`${API_PREFIX}/progress/${effectiveUserId}`);
      setProgress(await res.json());
    } catch (e) { console.error(e); }
  };
  const fetchSkillAreas = async () => {
    try {
      const res = await fetch(`${API_PREFIX}/skill-areas`);
      setSkillAreas(await res.json());
    } catch (e) { console.error(e); }
  };
  const fetchPlatformStats = async () => {
    try {
      const res = await fetch(`${API_PREFIX}/stats`);
      setPlatformStats(await res.json());
    } catch (e) { console.error(e); }
  };
  const fetchExamTips = async () => {
    try {
      const res = await fetch(`${API_PREFIX}/exam-tips`);
      setExamTips(await res.json());
    } catch (e) { console.error(e); }
  };

  const startPractice = async (mode, skillArea = null) => {
    if (mode.id === 'weak' && !proUnlocked) {
      setPaywallFeature('Weak-Areas Practice');
      setPaywallOpen(true);
      return;
    }
    setPracticeMode(mode);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setResult(null);
    setPracticeStats({ correct: 0, total: 0 });

    try {
      let url = `${API_PREFIX}/questions?limit=${mode.questions || 10}`;
      if (skillArea) url += `&skill_area=${skillArea}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.questions?.length > 0) {
        setQuestions(data.questions);
        setActiveTab('practice');
      } else toast.error('No questions available');
    } catch (e) {
      toast.error('Failed to load questions');
    }
  };

  const startSimulation = async () => {
    if (!proUnlocked) {
      setPaywallFeature('Exam Simulation');
      setPaywallOpen(true);
      return;
    }
    try {
      const res = await fetch(`${API_PREFIX}/simulation/start?user_id=${effectiveUserId}`, { method: 'POST' });
      const data = await res.json();
      if (data.questions?.length > 0) {
        setQuestions(data.questions);
        setSimulationTime(data.time_limit || SIM_TIME);
        setSimulationAnswers({});
        setSimulationActive(true);
        setSimulationResult(null);
        setActiveTab('practice');
        setPracticeMode({ id: 'simulation', name: 'Exam Simulation' });
      }
    } catch (e) { toast.error('Failed to start simulation'); }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;
    try {
      const question = questions[currentQuestionIndex];
      const res = await fetch(`${API_PREFIX}/submit-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: question.id, answer: selectedAnswer, user_id: effectiveUserId })
      });
      const data = await res.json();
      setResult(data);
      setShowResult(true);
      setPracticeStats(prev => ({
        correct: prev.correct + (data.correct ? 1 : 0),
        total: prev.total + 1
      }));
    } catch (e) { toast.error('Failed to submit'); }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setResult(null);
    } else {
      toast.success(`Practice complete! Score: ${practiceStats.correct}/${practiceStats.total}`);
      exitPractice();
    }
  };

  const exitPractice = () => {
    setPracticeMode(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setResult(null);
    setSimulationActive(false);
    setActiveTab('dashboard');
    fetchProgress();
  };

  const submitSimulation = async () => {
    setSimulationActive(false);
    try {
      const res = await fetch(`${API_PREFIX}/simulation/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          simulation_id: `sim_${Date.now()}`,
          answers: simulationAnswers,
          user_id: effectiveUserId
        })
      });
      const data = await res.json();
      setSimulationResult(data);
      toast.success(`Simulation complete! Score: ${data.score}%`);
    } catch (e) { toast.error('Failed to submit simulation'); }
  };

  const loadFlashcards = async (skillArea = null) => {
    try {
      let url = `${API_PREFIX}/flashcards`;
      if (skillArea) url += `?skill_area=${skillArea}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.flashcards?.length > 0) {
        setFlashcards(data.flashcards);
        setCurrentFlashcard(0);
        setShowFlashcardBack(false);
        setPracticeMode({ id: 'flashcards', name: 'Flashcards' });
        setActiveTab('practice');
      } else toast.error('No flashcards available');
    } catch (e) { toast.error('Failed to load flashcards'); }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = progress?.total_questions > 0
    ? Math.round((progress.correct_answers / progress.total_questions) * 100)
    : 0;

  const readinessScore = Math.min(100, Math.round(
    (accuracy * 0.4) +
    ((progress?.simulations_completed || 0) * 10) +
    ((progress?.total_questions || 0) / 5)
  ));

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pt-20" data-testid="az104-exam-prep">
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Server className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">AZ-104 Exam Coach</h1>
                  <p className="text-blue-600 font-medium">Microsoft Azure Administrator Associate</p>
                </div>
              </div>
              <p className="text-slate-600 max-w-xl">
                Master Azure administration — identities & governance, compute, storage, networking, and monitoring — with AI-powered exam prep.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Associate level</Badge>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-sky-100">250+ questions</Badge>
                <Badge variant="outline">100 min · 40-60 Qs · 700/1000 to pass</Badge>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {authLoading ? (
                <div className="animate-pulse bg-slate-200 h-10 w-32 rounded-lg"></div>
              ) : isAuthenticated ? (
                <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200">
                  <User className="w-5 h-5 text-blue-600" />
                  <span className="text-slate-700 font-medium">{user?.first_name || user?.email}</span>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500 hover:text-slate-700">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => openAuth('login')} className="border-blue-200 text-blue-700" data-testid="az104-login-btn">
                    <LogIn className="w-4 h-4 mr-2" /> Sign In
                  </Button>
                  <Button onClick={() => openAuth('register')} className="bg-blue-600 hover:bg-blue-700" data-testid="az104-signup-btn">
                    Sign Up Free
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Readiness Score</p>
                    <p className="text-2xl font-bold">{readinessScore}%</p>
                  </div>
                  <Target className="w-8 h-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Day Streak</p>
                    <p className="text-2xl font-bold">{progress?.streak || 0}</p>
                  </div>
                  <Flame className="w-8 h-8 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Questions Done</p>
                    <p className="text-2xl font-bold">{progress?.total_questions || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-100 text-sm">Simulations</p>
                    <p className="text-2xl font-bold">{progress?.simulations_completed || 0}</p>
                  </div>
                  <Timer className="w-8 h-8 text-violet-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white shadow-sm border border-slate-200 p-1 rounded-lg">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">Dashboard</TabsTrigger>
              <TabsTrigger value="practice" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">Practice</TabsTrigger>
              <TabsTrigger value="tips" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">Exam Tips</TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-blue-600" /> Start Practicing</CardTitle>
                      <CardDescription>Choose a practice mode to begin</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {PRACTICE_MODES.map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => {
                              if (mode.id === 'simulation') requirePro('Exam Simulation', startSimulation);
                              else if (mode.id === 'weak') requirePro('Weak-Areas Practice', () => startPractice(mode));
                              else if (mode.id === 'flashcards') loadFlashcards();
                              else startPractice(mode);
                            }}
                            className="p-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                            data-testid={`practice-mode-${mode.id}`}
                          >
                            <div className={`w-10 h-10 ${mode.color} rounded-lg flex items-center justify-center mb-3`}>
                              <mode.icon className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="font-semibold text-slate-900 group-hover:text-blue-700">{mode.name}</h3>
                            <p className="text-sm text-slate-500">{mode.desc}</p>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Skill Areas</CardTitle>
                      <CardDescription>Track your progress by exam domain</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(skillAreas).map(([key, area]) => (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">{area.name}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{area.weight}%</Badge>
                                <Button variant="ghost" size="sm" onClick={() => startPractice(PRACTICE_MODES[1], key)} className="text-blue-600 hover:text-blue-700 text-xs">
                                  Practice <ChevronRight className="w-3 h-3 ml-1" />
                                </Button>
                              </div>
                            </div>
                            <Progress value={Math.random() * 100} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-6">
                  <Card className="bg-gradient-to-br from-blue-600 to-cyan-700 text-white border-0">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-lg mb-4">SC-300 Exam Info</h3>
                      <div className="space-y-3 text-blue-100">
                        <div className="flex justify-between"><span>Duration:</span><span className="font-medium text-white">100 minutes</span></div>
                        <div className="flex justify-between"><span>Questions:</span><span className="font-medium text-white">40-60 questions</span></div>
                        <div className="flex justify-between"><span>Passing Score:</span><span className="font-medium text-white">700/1000 (70%)</span></div>
                        <div className="flex justify-between"><span>Exam Cost:</span><span className="font-medium text-white">$165 USD <span className="text-xs text-blue-200">(Microsoft fee)</span></span></div>
                      </div>
                    </CardContent>
                  </Card>

                  {platformStats && (
                    <Card>
                      <CardHeader><CardTitle className="text-sm">Question Bank</CardTitle></CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600 mb-2">{platformStats.total_questions}+</div>
                        <p className="text-sm text-slate-500">Practice questions available</p>
                        <div className="mt-4 space-y-2">
                          {Object.entries(platformStats.by_difficulty || {}).map(([diff, count]) => (
                            <div key={diff} className="flex justify-between text-sm">
                              <span className="capitalize text-slate-600">{diff}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="practice">
              {practiceMode ? (
                <div className="max-w-3xl mx-auto">
                  {practiceMode.id === 'simulation' && simulationActive && (
                    <div className="mb-4 flex items-center justify-between bg-sky-100 px-4 py-2 rounded-lg">
                      <span className="text-blue-700 font-medium">Exam Simulation in Progress</span>
                      <div className="flex items-center gap-4">
                        <span className={`font-mono font-bold ${simulationTime < 300 ? 'text-red-600' : 'text-blue-700'}`}>{formatTime(simulationTime)}</span>
                        <Button onClick={submitSimulation} variant="outline" className="border-blue-300 text-blue-700">Submit Exam</Button>
                      </div>
                    </div>
                  )}

                  {simulationResult && (
                    <Card className="mb-6">
                      <CardContent className="p-6 text-center">
                        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${simulationResult.passed ? 'bg-green-100' : 'bg-red-100'}`}>
                          {simulationResult.passed ? <CheckCircle className="w-10 h-10 text-green-600" /> : <XCircle className="w-10 h-10 text-red-600" />}
                        </div>
                        <h2 className="text-2xl font-bold mb-2">{simulationResult.passed ? 'Congratulations!' : 'Keep Practicing!'}</h2>
                        <p className="text-4xl font-bold text-blue-600 mb-2">{simulationResult.score}%</p>
                        <p className="text-slate-600 mb-4">{simulationResult.correct_answers} out of {simulationResult.total_questions} correct</p>
                        <p className={`text-sm ${simulationResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                          {simulationResult.passed ? "You passed! You're ready for the real exam." : `Passing score is ${simulationResult.passing_score}%. Don't give up!`}
                        </p>
                        <Button onClick={exitPractice} className="mt-6 bg-blue-600 hover:bg-blue-700">Return to Dashboard</Button>
                      </CardContent>
                    </Card>
                  )}

                  {practiceMode.id === 'flashcards' && flashcards.length > 0 && (
                    <Card className="min-h-[400px]">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-center mb-6">
                          <Badge variant="outline">{currentFlashcard + 1} / {flashcards.length}</Badge>
                          <Button variant="ghost" size="sm" onClick={exitPractice}>Exit Flashcards</Button>
                        </div>
                        <div className="min-h-[300px] bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 cursor-pointer flex flex-col justify-center" onClick={() => setShowFlashcardBack(!showFlashcardBack)}>
                          {!showFlashcardBack ? (
                            <div className="text-center">
                              <p className="text-lg text-slate-700">{flashcards[currentFlashcard]?.front}</p>
                              <p className="text-sm text-slate-400 mt-4">Click to reveal answer</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <p className="text-lg text-blue-700 font-medium whitespace-pre-line">{flashcards[currentFlashcard]?.back}</p>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between mt-6">
                          <Button variant="outline" disabled={currentFlashcard === 0} onClick={() => { setCurrentFlashcard(prev => prev - 1); setShowFlashcardBack(false); }}>Previous</Button>
                          <Button className="bg-blue-600 hover:bg-blue-700" disabled={currentFlashcard === flashcards.length - 1} onClick={() => { setCurrentFlashcard(prev => prev + 1); setShowFlashcardBack(false); }}>Next</Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {practiceMode.id !== 'flashcards' && questions.length > 0 && !simulationResult && (
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-center mb-6">
                          <Badge variant="outline">Question {currentQuestionIndex + 1} / {questions.length}</Badge>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Score: {practiceStats.correct}/{practiceStats.total}</span>
                            <Button variant="ghost" size="sm" onClick={exitPractice}>Exit Practice</Button>
                          </div>
                        </div>
                        <div className="mb-6">
                          <Badge className="mb-3 bg-blue-100 text-blue-700">{questions[currentQuestionIndex]?.skill_area?.replace(/_/g, ' ').toUpperCase()}</Badge>
                          <h3 className="text-lg font-medium text-slate-900">{questions[currentQuestionIndex]?.question}</h3>
                        </div>
                        <RadioGroup value={selectedAnswer?.toString()} onValueChange={(v) => !showResult && setSelectedAnswer(parseInt(v))} className="space-y-3">
                          {questions[currentQuestionIndex]?.options?.map((option, index) => (
                            <div key={index} className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all ${
                              showResult
                                ? index === questions[currentQuestionIndex]?.correct_answer ? 'border-green-500 bg-green-50'
                                  : selectedAnswer === index ? 'border-red-500 bg-red-50' : 'border-slate-200'
                                : selectedAnswer === index ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                            }`}>
                              <RadioGroupItem value={index.toString()} id={`option-${index}`} disabled={showResult} />
                              <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">{option}</Label>
                              {showResult && index === questions[currentQuestionIndex]?.correct_answer && <CheckCircle className="w-5 h-5 text-green-600" />}
                              {showResult && selectedAnswer === index && index !== questions[currentQuestionIndex]?.correct_answer && <XCircle className="w-5 h-5 text-red-600" />}
                            </div>
                          ))}
                        </RadioGroup>
                        {showResult && result && (
                          <div className={`mt-6 p-4 rounded-lg ${result.correct ? 'bg-green-50' : 'bg-amber-50'}`}>
                            <div className="flex items-start gap-3">
                              <Lightbulb className={`w-5 h-5 mt-0.5 ${result.correct ? 'text-green-600' : 'text-amber-600'}`} />
                              <div>
                                <p className="font-medium text-slate-900">Explanation</p>
                                <p className="text-sm text-slate-600 mt-1">{result.explanation}</p>
                                {result.exam_tip && <p className="text-sm text-blue-700 mt-2"><strong>Exam Tip:</strong> {result.exam_tip}</p>}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="mt-6 flex justify-end gap-3">
                          {!showResult ? (
                            <Button onClick={submitAnswer} disabled={selectedAnswer === null} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-answer-btn">Submit Answer</Button>
                          ) : (
                            <Button onClick={nextQuestion} className="bg-blue-600 hover:bg-blue-700">{currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Practice'}</Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Server className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Ready to Practice?</h3>
                  <p className="text-slate-500 mb-6">Choose a practice mode from the dashboard to get started</p>
                  <Button onClick={() => setActiveTab('dashboard')} className="bg-blue-600 hover:bg-blue-700">Go to Dashboard</Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tips">
              <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Exam Tips</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {examTips?.tips?.map((tip, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> High-Frequency Topics</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {examTips?.high_frequency_topics?.map((topic, i) => (
                        <div key={i} className="p-3 bg-blue-50 rounded-lg">
                          <Badge variant="outline" className="mb-1 text-xs">{topic.skill.replace(/_/g, ' ')}</Badge>
                          <p className="text-sm text-slate-700">{topic.topic}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Get Full Access</h2>
            <p className="text-slate-600">Unlock all features and maximize your SC-300 preparation</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-slate-200">
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <CardDescription>Get started with basics</CardDescription>
                <div className="text-3xl font-bold text-slate-900">$0</div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 20 practice questions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Basic flashcards</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Progress tracking</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-blue-600">Associate-level</Badge></div>
              <CardHeader>
                <CardTitle>Pro</CardTitle>
                <CardDescription>Full AZ-104 exam prep</CardDescription>
                <div className="text-3xl font-bold text-blue-600">${CERT_PRICE}<span className="text-base font-normal text-slate-500"> one-time</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> All 250+ practice questions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Unlimited 55-Q simulations</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> AI Coach support</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Weak-area analytics</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 2 months access</li>
                </ul>
                {proUnlocked ? (
                  <Button disabled className="w-full mt-4 bg-green-600 hover:bg-green-600 text-white" data-testid="pro-already-unlocked">
                    <CheckCircle className="w-4 h-4 mr-2" /> Pro — Unlocked
                  </Button>
                ) : (
                  <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => { setPaywallFeature('Pro Plan'); setPaywallOpen(true); }} data-testid="upgrade-to-pro-btn">
                    Unlock for ${CERT_PRICE}
                  </Button>
                )}
              </CardContent>
            </Card>
            <Card className="border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-white">
              <CardHeader>
                <CardTitle>6-Cert Track</CardTitle>
                <CardDescription>The Microsoft AI &amp; Identity bundle</CardDescription>
                <div className="text-3xl font-bold text-violet-700">View<span className="text-base font-normal text-slate-500"> →</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> AI-900, AI-102, AZ-900</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> DP-100, AB-900, SC-300</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Roadmap & study plan</li>
                </ul>
                <Link to="/microsoft-track"><Button variant="outline" className="w-full mt-4 border-violet-400 text-violet-700 hover:bg-violet-50" data-testid="view-microsoft-track-btn">
                  View Roadmap <ArrowRight className="w-4 h-4 ml-2" />
                </Button></Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} defaultTab={authModalTab} certName="AZ-104" />
      <CertPaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        certCode={CERT_CODE}
        certName={CERT_NAME}
        amount={CERT_PRICE}
        defaultEmail={proEmail}
        defaultName={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}
        feature={paywallFeature}
        simQuestionCount={55}
      />
    </div>
  );
}
